import type {
  BarometerSuccess,
} from '@autobid/strapi-integration/typescript/auction/barometerCar'
import type { ItemPublishEvent } from '@autobid/ui/types/websocket/ItemPublishEvent'
import type { Ref } from 'vue'
import { useWebSocketStore } from '@autobid/nuxt-pinia-store/store/useWebSocketStore'
import {
  getBarometerQueryKey,
} from '@autobid/ui/composables/car/data/useBarometerCar'
import { BAROMETER_SOCKET_CALLBACK } from '@autobid/ui/composables/car/sockets/useBarometerSocket'
import { randomDebounce } from '@autobid/ui/utils/randomDebounce'
import { useQueryClient } from '@tanstack/vue-query'

export function useBarometerItemPublish() {
  const { startListen } = useWebSocketStore()
  const queryClient = useQueryClient()

  const itemPublishDebounce = useState(
    'barometer-item-publish-debounce',
    () => ({}),
  ) as Ref<Record<number, NodeJS.Timeout>>

  const itemPublish = () => {
    const invalidateCarInAuction = (auctionId: number) => {
      const queryKey = getBarometerQueryKey({
        type: 'current',
        auctionId,
      })
      queryClient.invalidateQueries(queryKey)
    }

    const invalidateBarometers = (event: ItemPublishEvent) => {
      const { auctionId, itemId } = event.context
      const currentBarometerQueryKey = getBarometerQueryKey({
        type: 'current',
        auctionId,
      })
      const barometerQueryBaseKey = getBarometerQueryKey({ type: 'item' })[0]

      queryClient.invalidateQueries(currentBarometerQueryKey)
      queryClient.invalidateQueries({
        predicate: (query) => {
          if (!Array.isArray(query.queryKey))
            return false

          if (query.queryKey[0] !== barometerQueryBaseKey)
            return false

          const queryData = query.state
            .data as BarometerSuccess['data']['singleItem']

          if (!queryData)
            return false

          if (queryData.item.itemData.auctionId === auctionId)
            return true

          return queryData.item.itemData.id === itemId
        },
      })
    }

    startListen({
      eventName: 'premium.ITEM_PUBLISH',
      callbackName: BAROMETER_SOCKET_CALLBACK,
      callback: (event) => {
        clearTimeout(itemPublishDebounce.value[event.context.auctionId])

        itemPublishDebounce.value[event.context.auctionId] = randomDebounce(
          1000,
          3000,
          () => {
            invalidateCarInAuction(event.context.auctionId)
            invalidateBarometers(event)
          },
        )
      },
    })
  }

  return { itemPublish }
}
